<script>
import ListHeader from '@/components/common/ListHeader.vue';
import FilterChips from '@/components/common/FilterChips.vue';
import ListPagination from '@/components/common/ListPagination.vue';
import SurveyFilters from '@/components/surveys/SurveyFilters.vue';
import SurveyTable from '@/components/surveys/SurveyTable.vue';
import DeleteSurveyModal from '@/components/surveys/DeleteSurveyModal.vue';
import surveyService from '@/shared/services/survey.service';
import notificationService from '@/shared/services/notification.service';
import {
  countActiveFilters,
  fromCriteria,
  toCriteria,
} from '@/shared/utils/list-filters';

/**
 * L'etat de repos de chaque critere. Tout ce qui s'en ecarte filtre, compte
 * dans la pastille du panneau, porte une pastille sous la barre, et part dans
 * le prereglage qu'on enregistre.
 */
const EMPTY_FILTERS = {
  searchQuery: '',
  statuses: [],
  governorates: [],
  sectors: [],
  referenceYears: [],
  createdByIds: [],
  hasRespondent: '',
  interviewDate: { from: null, to: null },
  dateAdded: { from: null, to: null },
  sort: 'recent',
};

const STATUS_LABELS = { SUBMITTED: 'Soumise', DRAFT: 'Brouillon' };

// Maps onto the order[field]/order[direction] pair the API already accepts.
const SORTS = {
  recent: { field: 'createdAt', direction: 'desc' },
  oldest: { field: 'createdAt', direction: 'asc' },
  status: { field: 'status', direction: 'asc' },
};

export default {
  name: 'ListSurveys',
  components: {
    ListHeader,
    FilterChips,
    ListPagination,
    SurveyFilters,
    SurveyTable,
    DeleteSurveyModal,
  },
  data() {
    return {
      surveys: [],
      loading: false,
      error: null,
      exporting: false,
      activeTab: 'all',
      filterTimeout: null,
      filters: { ...EMPTY_FILTERS },
      appliedFilters: { ...EMPTY_FILTERS },
      filterOptions: {
        governorates: [],
        sectors: [],
        referenceYears: [],
        authors: [],
      },
      panelOpen: false,
      appliedFilterId: null,
      // Rappeler un prereglage applique ses criteres tout de suite; la
      // temporisation de saisie n'a pas a relancer la meme recherche 400 ms
      // plus tard.
      skipNextSchedule: false,
      currentPage: 1,
      perPage: 10,
      totalSurveys: 0,
      totalPages: 0,
      deleteModal: false,
      submissionToDelete: null,
    };
  },
  computed: {
    tabs() {
      return [
        { key: 'all', label: 'Toutes les enquêtes' },
        { key: 'mine', label: 'Mes enquêtes' },
      ];
    },

    activeCount() {
      return countActiveFilters(this.filters, EMPTY_FILTERS);
    },

    /** Ce qu'on enregistre sous un nom: les criteres qui filtrent, rien d'autre. */
    criteria() {
      return toCriteria(this.filters, EMPTY_FILTERS);
    },

    chips() {
      const out = [];
      const f = this.appliedFilters;

      if (f.searchQuery) {
        out.push({ key: 'searchQuery', label: `Recherche : ${f.searchQuery}` });
      }
      if (f.statuses?.length) {
        const labels = f.statuses.map((s) => STATUS_LABELS[s] || s);
        out.push({ key: 'statuses', label: `Statuts : ${labels.join(', ')}` });
      }
      if (f.governorates?.length) {
        out.push({
          key: 'governorates',
          label: `Gouvernorats : ${f.governorates.join(', ')}`,
        });
      }
      if (f.sectors?.length) {
        out.push({
          key: 'sectors',
          label: `Secteurs : ${f.sectors.join(', ')}`,
        });
      }
      if (f.referenceYears?.length) {
        out.push({
          key: 'referenceYears',
          label: `Exercices : ${f.referenceYears.join(', ')}`,
        });
      }
      if (f.createdByIds?.length) {
        const labels = f.createdByIds.map(
          (id) =>
            this.filterOptions.authors?.find((author) => author.id === id)
              ?.name ?? `#${id}`,
        );
        out.push({
          key: 'createdByIds',
          label: `Saisies par : ${labels.join(', ')}`,
        });
      }
      if (f.hasRespondent) {
        out.push({
          key: 'hasRespondent',
          label:
            f.hasRespondent === 'yes'
              ? 'Répondant renseigné'
              : 'Répondant non renseigné',
        });
      }
      if (f.interviewDate?.from || f.interviewDate?.to) {
        out.push({
          key: 'interviewDate',
          label: `Entretien ${this.rangeLabel(f.interviewDate)}`,
        });
      }
      if (f.dateAdded?.from || f.dateAdded?.to) {
        out.push({
          key: 'dateAdded',
          label: `Création ${this.rangeLabel(f.dateAdded)}`,
        });
      }

      return out;
    },

    hasFilters() {
      return this.chips.length > 0;
    },
  },
  mounted() {
    this.fetchSurveys();
    this.fetchFilterOptions();
  },
  beforeUnmount() {
    if (this.filterTimeout) {
      clearTimeout(this.filterTimeout);
    }
  },
  methods: {
    /** « du … au … », « depuis le … » ou « jusqu'au … » selon les bornes posees. */
    rangeLabel(range) {
      if (range.from && range.to) {
        return `du ${range.from} au ${range.to}`;
      }

      return range.from ? `depuis le ${range.from}` : `jusqu'au ${range.to}`;
    },

    /** Les criteres actifs, traduits en parametres que l'API lit. */
    queryParams() {
      const f = this.appliedFilters;

      const params = {
        searchText: f.searchQuery || undefined,
        statuses: f.statuses?.length ? f.statuses : undefined,
        governorates: f.governorates?.length ? f.governorates : undefined,
        sectors: f.sectors?.length ? f.sectors : undefined,
        referenceYears: f.referenceYears?.length ? f.referenceYears : undefined,
        createdByIds: f.createdByIds?.length ? f.createdByIds : undefined,
        hasRespondent: f.hasRespondent ? f.hasRespondent === 'yes' : undefined,
        dateFrom: f.interviewDate?.from || undefined,
        dateTo: f.interviewDate?.to || undefined,
        createdAtFrom: f.dateAdded?.from || undefined,
        createdAtTo: f.dateAdded?.to || undefined,
        createdByMe: this.activeTab === 'mine' || undefined,
      };

      Object.keys(params).forEach((key) => {
        if (params[key] === undefined) {
          delete params[key];
        }
      });

      return params;
    },

    /**
     * Ce que les choix multiples proposent vient de la base: une liste ecrite
     * en dur proposerait des gouvernorats ou des exercices qui ne rendent
     * rien. Un echec laisse les listes vides plutot que de bloquer l'ecran.
     */
    async fetchFilterOptions() {
      try {
        this.filterOptions = await surveyService.getFilterOptions();
      } catch (err) {
        this.filterOptions = {
          governorates: [],
          sectors: [],
          referenceYears: [],
          authors: [],
        };
      }
    },

    async fetchSurveys() {
      this.loading = true;
      this.error = null;

      try {
        const order = SORTS[this.appliedFilters.sort] ?? SORTS.recent;

        const payload = await surveyService.getSurveys({
          page: Number(this.currentPage) || 1,
          limit: Number(this.perPage) || 10,
          ...this.queryParams(),
          'order[field]': order.field,
          'order[direction]': order.direction,
        });

        if (payload?.data) {
          this.surveys = Array.isArray(payload.data) ? payload.data : [];
          this.totalSurveys =
            payload.meta?.total ?? payload.total ?? this.surveys.length;
          this.totalPages =
            payload.meta?.totalPages ??
            Math.ceil(this.totalSurveys / this.perPage);
        } else if (Array.isArray(payload)) {
          this.surveys = payload;
          this.totalSurveys = payload.length;
          this.totalPages = 1;
        } else {
          this.surveys = payload?.items ?? [];
          this.totalSurveys = payload?.meta?.total ?? this.surveys.length;
          this.totalPages =
            payload?.meta?.totalPages ??
            Math.ceil(this.totalSurveys / this.perPage);
        }
      } catch (err) {
        const friendly = this.formatApiError(err);
        this.error = friendly.message;

        if (friendly.validationMessages?.length) {
          this.showValidationToast(friendly.validationMessages.join(' — '));
        } else {
          this.showErrorToast(friendly.message);
        }

        this.surveys = [];
        this.totalSurveys = 0;
        this.totalPages = 0;
      } finally {
        this.loading = false;
      }
    },

    // Filters apply on their own; the chips report what is active.
    scheduleFetch() {
      if (this.skipNextSchedule) {
        this.skipNextSchedule = false;
        return;
      }

      if (this.filterTimeout) {
        clearTimeout(this.filterTimeout);
      }

      // Une main sur les criteres s'ecarte du prereglage rappele: la liste
      // deroulante ne doit plus pretendre le montrer.
      this.appliedFilterId = null;

      this.filterTimeout = setTimeout(() => {
        this.applyNow();
      }, 400);
    },

    applyNow() {
      this.appliedFilters = { ...this.filters };
      this.currentPage = 1;
      this.fetchSurveys();
    },

    /** Rappelle un prereglage: ses criteres sont reposes, la liste se relance. */
    applySavedFilter(filter) {
      if (!filter) {
        return;
      }

      if (this.filterTimeout) {
        clearTimeout(this.filterTimeout);
      }

      this.skipNextSchedule = true;
      this.filters = fromCriteria(filter.criteria, EMPTY_FILTERS);
      this.appliedFilterId = filter.id;
      this.panelOpen = countActiveFilters(this.filters, EMPTY_FILTERS) > 0;
      this.applyNow();
    },

    /**
     * Le prereglage marque par defaut s'applique a l'ouverture, mais jamais
     * par-dessus des criteres deja regles a la main.
     */
    onSavedFiltersLoaded(defaultFilter) {
      if (defaultFilter && this.activeCount === 0) {
        this.applySavedFilter(defaultFilter);
      }
    },

    clearFilter(key) {
      this.skipNextSchedule = true;
      this.filters = { ...this.filters, [key]: EMPTY_FILTERS[key] };
      this.appliedFilterId = null;
      this.applyNow();
    },

    clearAllFilters() {
      this.skipNextSchedule = true;
      this.filters = { ...EMPTY_FILTERS };
      this.appliedFilterId = null;
      this.applyNow();
    },

    changePage(page) {
      this.currentPage = page;
      this.fetchSurveys();
    },

    changeTab(tab) {
      this.activeTab = tab;
      this.currentPage = 1;
      this.fetchSurveys();
    },

    editSurvey(id) {
      this.$router.push({ name: 'survey-edit', params: { id } });
    },

    confirmDelete(submission) {
      this.submissionToDelete = submission;
      this.deleteModal = true;
    },

    onDeleted() {
      if (this.surveys.length === 1 && this.currentPage > 1) {
        this.currentPage -= 1;
      }

      this.fetchSurveys();
      this.submissionToDelete = null;
      this.deleteModal = false;
    },

    formatApiError(error) {
      if (!error) {
        return { message: 'Erreur inconnue', validationMessages: [] };
      }

      if (error.response?.data) {
        const d = error.response.data;

        if (Array.isArray(d.message)) {
          return {
            message: d.error || 'Erreur de validation',
            validationMessages: d.message,
          };
        }
        if (typeof d.message === 'string') {
          return { message: d.message, validationMessages: [] };
        }

        return { message: d.error || 'Erreur serveur', validationMessages: [] };
      }

      if (error.message) {
        return { message: error.message, validationMessages: [] };
      }

      return { message: 'Erreur inconnue', validationMessages: [] };
    },

    showErrorToast(msg) {
      notificationService.toastError(msg);
    },

    showValidationToast(msg) {
      notificationService.toastInfo(msg);
    },

    async exportSurveys() {
      if (this.exporting) {
        return;
      }

      this.exporting = true;

      try {
        const order = SORTS[this.appliedFilters.sort] ?? SORTS.recent;

        const blob = await surveyService.exportSurveys({
          page: 1,
          limit: 10000,
          ...this.queryParams(),
          'order[field]': order.field,
          'order[direction]': order.direction,
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
          'download',
          `surveys_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.xlsx`,
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        const friendly = this.formatApiError(err);
        this.showErrorToast(
          "Erreur lors de l'exportation : " + friendly.message,
        );
      } finally {
        this.exporting = false;
      }
    },

    navigateToSurvey() {
      this.$router.push({ name: 'Survey' });
    },
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.scheduleFetch();
      },
    },
  },
};
</script>

<template>
  <div class="survey-list-container mt-4">
    <div class="list-card">
      <ListHeader :tabs="tabs" :active-tab="activeTab" @tab-change="changeTab">
        <template #actions>
          <button
            type="button"
            class="list-btn list-btn--primary"
            @click="navigateToSurvey"
          >
            <i class="ri-add-line"></i>
            Ajouter une enquête
          </button>
          <button
            type="button"
            class="list-btn list-btn--ghost"
            title="Exporter la liste des enquêtes"
            :disabled="exporting"
            @click="exportSurveys"
          >
            <i class="ri-file-excel-line"></i>
            {{ exporting ? 'Export…' : 'Exporter' }}
          </button>
        </template>
      </ListHeader>

      <SurveyFilters
        v-model="filters"
        v-model:expanded="panelOpen"
        :options="filterOptions"
        :active-count="activeCount"
        :criteria="criteria"
        :applied-filter-id="appliedFilterId"
        @reset="clearAllFilters"
        @apply-saved="applySavedFilter"
        @saved-filters-loaded="onSavedFiltersLoaded"
      />

      <FilterChips
        :chips="chips"
        @clear="clearFilter"
        @clear-all="clearAllFilters"
      />

      <SurveyTable
        :surveys="surveys"
        :loading="loading"
        :error="error"
        :has-filters="hasFilters"
        @edit="editSurvey"
        @delete="confirmDelete"
        @clear-filters="clearAllFilters"
      />

      <ListPagination
        :current-page="currentPage"
        :per-page="perPage"
        :total="totalSurveys"
        :total-pages="totalPages"
        noun="enquêtes"
        @page-change="changePage"
      />
    </div>

    <DeleteSurveyModal
      v-model="deleteModal"
      :submission="submissionToDelete"
      @deleted="onDeleted"
    />
  </div>
</template>
